/**
 * Photo Slice - capture/floor-plan/pin/GPS/watermark/image-processing state.
 *
 * Owns the lifecycle of a single photo from capture through watermark export:
 *   - the captured/final image data URIs
 *   - the floor plan selected for this photo (mirrored to localStorage and to
 *     a per-project "last used" memory so a returning user re-opens the same plan)
 *   - the pin position/direction on that floor plan (clamped to valid ranges)
 *   - live GPS coordinates, voice comments, and watermark settings
 *
 * Runtime behaviour here is load-bearing and intentionally preserved:
 *   - `setFloorPlanImage` persists to localStorage key 'floorPlanImage' AND to
 *     per-project memory key 'pinCam_lastFloorPlanByProject'.
 *   - pin position is clamped to [0,100]; pin direction to [0,360].
 *   - `fetchGps` only requests location on a secure origin (HTTPS / localhost /
 *     private LAN ranges) and maps the geolocation error codes to user-facing
 *     copy.
 *   - `getDefaultPinState()` centres the pin at (50,50).
 */

import { logger } from '../utils/logger';
import { validatePinState, getDefaultPinState } from '../utils/image';
import { PhotoState, PhotoActions } from '@/types/store';
import {
  PinState,
  GpsData,
  ImageProcessingState,
  WatermarkSettings,
  MapViewState,
  FloorPlan,
} from '@/types/index';
import { safeStorage } from '../utils/safeStorage';

// A floor plan as stored on the photo slice: the server FloorPlan plus any
// extra client-side fields that may have been attached (kept permissive to
// match the `(FloorPlan & Record<string, any>) | null` shape in store types).
type StoredFloorPlan = (FloorPlan & Record<string, unknown>) | null;

/**
 * Read the floor plan persisted in localStorage at boot. Returns null on a
 * missing/corrupt value, removing the bad entry so we don't keep failing.
 */
const getInitialFloorPlan = (): StoredFloorPlan => {
  try {
    const item = safeStorage.getItem('floorPlanImage');
    logger.debug('Initial floor plan from storage:', { floorPlan: item || 'null' });
    if (item === null) {
      return null;
    }
    const parsedItem = JSON.parse(item) as StoredFloorPlan;
    logger.debug('Parsed initial floor plan:', { floorPlan: parsedItem });
    return parsedItem;
  } catch (error) {
    logger.error('Failed to parse floorPlanImage from storage:', { error });
    logger.debug('Problematic storage value:', { value: safeStorage.getItem('floorPlanImage') || 'null' });
    safeStorage.removeItem('floorPlanImage');
    return null;
  }
};

export const photoSlice: PhotoState = {
  capturedPhoto: null,
  floorPlanImage: getInitialFloorPlan(),
  finalImage: null,
  comment: '',
  pinState: getDefaultPinState(),
  mapViewState: { zoom: 1, pan: { x: 0, y: 0 } } as MapViewState,
  gps: { lat: null, lng: null, error: null } as GpsData,
  // Photo management state
  positionData: null,
  voiceComments: [],
  isProcessing: false,
  finalWatermarkedImage: null,
  currentPhotoId: null,
  fileInputRef: null,
  // Enhanced state for image processing
  imageProcessingState: {
    isGenerating: false,
    error: null,
    progress: 0,
    lastGenerated: null,
  } as ImageProcessingState,
  // Watermark settings
  watermarkSettings: {
    enabled: true,
    position: 'bottom-right',
    text: '',
    opacity: 0.7,
    fontSize: 48,
  } as WatermarkSettings,
};

// Storage key for per-project floor plan memory
const FLOOR_PLAN_MEMORY_KEY = 'pinCam_lastFloorPlanByProject';

/**
 * Remember the last selected floor plan id for a project so the floor-plan
 * picker can pre-select it next time the user opens this project.
 */
function saveFloorPlanForProject(projectId: number, floorPlanId: number): void {
  try {
    const stored = safeStorage.getItem(FLOOR_PLAN_MEMORY_KEY);
    const memory: Record<string, number> = stored ? JSON.parse(stored) : {};
    memory[String(projectId)] = floorPlanId;
    safeStorage.setItem(FLOOR_PLAN_MEMORY_KEY, JSON.stringify(memory));
    logger.debug('Saved floor plan selection', { projectId, floorPlanId });
  } catch (error) {
    logger.warn('Failed to save floor plan selection', { projectId, floorPlanId, error });
  }
}

// Local view of the immer-mutable photo slice. The store's `set` callback hands
// us a draft typed as `any` (see store types: full typing creates a circular
// reference through AppState), so we narrow only the fields we mutate here.
interface PhotoDraft {
  gps: GpsData;
  pinState: PinState;
  voiceComments: string[];
  imageProcessingState: ImageProcessingState;
  watermarkSettings: WatermarkSettings;
}

export const photoActions: PhotoActions = (set, _get) => ({
  setCapturedPhoto: (photo: string | null): void => {
    logger.debug('Setting captured photo');
    set({ capturedPhoto: photo });
  },

  setFloorPlanImage: (image: StoredFloorPlan): void => {
    logger.debug('Setting floor plan image in localStorage and store:', { image });
    try {
      safeStorage.setItem('floorPlanImage', JSON.stringify(image));
      set({ floorPlanImage: image });

      // Save the selection for the current project (per-project memory)
      if (image?.id && image?.project_id) {
        saveFloorPlanForProject(image.project_id, image.id);
      }
    } catch (error) {
      logger.error('Failed to save floor plan image to localStorage:', { error });
      set({ floorPlanImage: image });
    }
  },

  setFinalImage: (image: string | null): void => {
    logger.debug('Setting final image');
    set({ finalImage: image });
  },

  setComment: (comment: string): void => set({ comment }),

  setPinState: (pinState: PinState): void => {
    logger.debug('Setting pin state:', { pinState });
    if (validatePinState(pinState)) {
      set({ pinState });
    } else {
      logger.warn('Invalid pin state provided:', { pinState });
      set({ pinState: getDefaultPinState() });
    }
  },

  setMapViewState: (mapViewState: MapViewState): void => {
    logger.debug('Setting map view state:', { mapViewState });
    set({ mapViewState });
  },

  setGps: (gps: GpsData): void => {
    logger.debug('Setting GPS data:', { gps });
    set((state: PhotoDraft) => { state.gps = gps; });
  },

  // Enhanced pin state management
  updatePinPosition: (x: number, y: number): void => {
    set((state: PhotoDraft) => {
      const newPinState: PinState = {
        ...state.pinState,
        pos: { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) },
      };

      return { pinState: newPinState };
    });
  },

  updatePinDirection: (direction: number): void => {
    set((state: PhotoDraft) => {
      const newPinState: PinState = {
        ...state.pinState,
        direction: Math.max(0, Math.min(360, direction)),
      };

      return { pinState: newPinState };
    });
  },

  resetPinState: (): void => {
    set({ pinState: getDefaultPinState() });
  },

  // Enhanced image processing state management
  setImageProcessingState: (processingState: Partial<ImageProcessingState>): void => {
    set((state: PhotoDraft) => ({
      imageProcessingState: { ...state.imageProcessingState, ...processingState },
    }));
  },

  setImageProcessingError: (error: string | Error): void => {
    logger.error('Setting image processing error:', { error });
    set((state: PhotoDraft) => ({
      imageProcessingState: {
        ...state.imageProcessingState,
        error: error instanceof Error ? error.message : error,
        isGenerating: false,
      },
    }));
  },

  clearImageProcessingError: (): void => {
    set((state: PhotoDraft) => ({
      imageProcessingState: {
        ...state.imageProcessingState,
        error: null,
      },
    }));
  },

  // Watermark settings management
  setWatermarkSettings: (settings: Partial<WatermarkSettings>): void => {
    set((state: PhotoDraft) => ({
      watermarkSettings: { ...state.watermarkSettings, ...settings },
    }));
  },

  // Photo management actions
  setPositionData: (positionData: unknown): void => set({ positionData }),

  setVoiceComments: (voiceComments: string[]): void => set({ voiceComments }),

  addVoiceComment: (comment: string): void => {
    set((state: PhotoDraft) => ({
      voiceComments: [...state.voiceComments, comment],
    }));
  },

  setIsProcessing: (isProcessing: boolean): void => set({ isProcessing }),

  setFinalWatermarkedImage: (image: string | null): void => set({ finalWatermarkedImage: image }),

  setCurrentPhotoId: (id: string | null): void => set({ currentPhotoId: id }),

  setFileInputRef: (ref: unknown): void => set({ fileInputRef: ref }),

  // Save photo with watermark function.
  // Lazily imports the heavy photoUtils module so the watermark/canvas code is
  // only pulled into the bundle when an export is actually requested.
  savePhotoWithWatermark: async (): Promise<unknown> => {
    const { savePhotoWithWatermark } = await import('../utils/photoUtils');
    return savePhotoWithWatermark();
  },

  // Enhanced reset function
  resetPhotoStates: (): void => {
    logger.info('Resetting all photo states');
    set({
      capturedPhoto: null,
      finalImage: null,
      comment: '',
      pinState: getDefaultPinState(),
      mapViewState: { zoom: 1, pan: { x: 0, y: 0 } } as MapViewState,
      positionData: null,
      voiceComments: [],
      currentPhotoId: null,
      finalWatermarkedImage: null,
      isProcessing: false,
      imageProcessingState: {
        isGenerating: false,
        error: null,
        progress: 0,
        lastGenerated: null,
      } as ImageProcessingState,
    });
  },

  // Enhanced GPS fetching with better error handling
  fetchGps: (): void => {
    logger.info('Requesting GPS permissions', {
      location: window.location.href,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
    });

    // Check if we're on HTTPS or localhost/local IP (browser considers these secure)
    const isSecure = window.location.protocol === 'https:' ||
                     window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.match(/^192\.168\./) ||
                     window.location.hostname.match(/^10\./) ||
                     window.location.hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./);

    logger.info('Security check', { isSecure });

    if (!isSecure) {
      logger.warn('Not on secure origin, GPS may not work. Using fallback coordinates.');
      set((state: PhotoDraft) => {
        state.gps = {
          lat: null,
          lng: null,
          error: 'GPS requires HTTPS or localhost. Please access via localhost or enable HTTPS.',
        };
      });
      return;
    }

    if (navigator.geolocation) {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      };

      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          logger.info('GPS position obtained successfully', {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });

          set((state: PhotoDraft) => {
            state.gps = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              error: null,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
            };
          });
        },
        (error: GeolocationPositionError) => {
          logger.error('GPS access error', { error: error.message, code: error.code });
          let errorMessage = 'GPS access denied';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'GPS permission denied. Please allow location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'GPS location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'GPS request timed out.';
              break;
            default:
              errorMessage = `GPS error: ${error.message}`;
          }
          set((state: PhotoDraft) => {
            state.gps = {
              lat: null,
              lng: null,
              error: errorMessage,
              code: error.code,
            };
          });
        },
        options,
      );
    } else {
      logger.warn('Geolocation not supported by browser');
      set((state: PhotoDraft) => {
        state.gps = {
          lat: null,
          lng: null,
          error: 'Geolocation is not supported by this browser.',
        };
      });
    }
  },
});
